import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  Settings,
  Palette,
  Bell,
  Key,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { usePayments } from '../../contexts/PaymentContext';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import { getHistory } from '../../utils/UserHistoryManager';
import { Download, ShieldCheck } from 'lucide-react';




/**
 * Componente de Configurações do Usuário
 * Permite editar perfil e preferências básicas
 */
const UserSettings = () => {
  const { user, updateUser } = useAuth();
  const { tickets } = useTickets();
  const { notifications } = useNotifications();
  const { punishments } = usePunishment();
  const { payments } = usePayments();
  const dynamicGradient = useDynamicGradient();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showDiscordId, setShowDiscordId] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    city: user?.city || '',
    notificationsEnabled: user?.notificationsEnabled !== undefined ? user.notificationsEnabled : true,
    notificationSounds: user?.notificationSounds !== undefined ? user.notificationSounds : true,
    showAnnouncements: user?.showAnnouncements !== undefined ? user.showAnnouncements : true,
    emailNotifications: user?.emailNotifications || false
  });

  // Função para lidar com mudanças nos inputs
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para salvar alterações
  const handleSave = async () => {
    try {
      await updateUser(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  };

  // Função para cancelar edição
  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      city: user?.city || '',
      notificationsEnabled: user?.notificationsEnabled !== undefined ? user.notificationsEnabled : true,
      notificationSounds: user?.notificationSounds !== undefined ? user.notificationSounds : true,
      showAnnouncements: user?.showAnnouncements !== undefined ? user.showAnnouncements : true,
      emailNotifications: user?.emailNotifications || false
    });
    setIsEditing(false);
  };

  // Função para navegar para configurações de tema
  const handleThemeSettings = () => {
    navigate('/settings/theme');
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo'
    });
  };

  // Função para baixar backup de dados (LGPD)
  const handleDownloadBackup = () => {
    if (!user) return;

    const backupData = {
      exportDate: new Date().toISOString(),
      userProfile: {
        id: user.id || user.discordId,
        name: user.name,
        email: user.email,
        discordId: user.discordId,
        accessLevel: user.accessLevel,
        createdAt: user.createdAt,
        cities: user.cities,
        settings: {
          notificationsEnabled: user.notificationsEnabled,
          notificationSounds: user.notificationSounds,
          showAnnouncements: user.showAnnouncements
        }
      },
      tickets: tickets.filter(t =>
        String(t.userId) === String(user.id) ||
        String(t.userDiscordId) === String(user.discordId) ||
        String(t.userId) === String(user.discordId)
      ),
      notifications: notifications || [],
      punishments: (punishments || []).filter(p =>
        String(p.userId) === String(user.discordId) ||
        String(p.userId) === String(user.id)
      ),
      payments: (payments || []).filter(p =>
        String(p.userId) === String(user.discordId) ||
        String(p.userId) === String(user.id)
      ),
      systemLogs: getHistory ? getHistory(user.discordId) : []
    };

    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backup_dados_capital_${user.name.replace(/\s+/g, '_').toLowerCase()}_${new Date().getTime()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-blue-900 animate-gradient-slow text-white p-6 space-y-4 rounded-3xl">
      {/* Header Moderno */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-black/40 to-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-4xl font-bold">
              <span className={`bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>
                Configurações
              </span>
            </h1>
            <p className="text-gray-400 mt-2 text-lg">Gerencie suas preferências e informações da conta</p>
          </div>

          <div className="flex items-center space-x-3 mt-4 sm:mt-0">

            {isEditing ? (
              <div className="flex space-x-2">
                <Button
                  onClick={handleSave}
                  className="bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Salvar
                </Button>
                <Button
                  onClick={handleCancel}
                  className="bg-gradient-to-r from-gray-500 to-slate-600 hover:from-gray-600 hover:to-slate-700 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <Edit className="h-4 w-4 mr-2" />
                Editar Perfil
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Informações do Perfil - Layout Compacto */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-3 space-y-4"
        >
          {/* Card de Informações Pessoais */}
          <Card className="bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center text-xl">
                <div className="p-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg mr-3">
                  <User className="h-5 w-5 text-blue-400" />
                </div>
                Informações Pessoais
              </CardTitle>
              <CardDescription className="text-gray-400">
                Suas informações básicas de perfil
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-300">Nome Completo</Label>
                  {isEditing ? (
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="bg-white/5 border-white/10 text-white mt-2 rounded-lg"
                      placeholder="Seu nome completo"
                    />
                  ) : (
                    <p className="text-white mt-2 font-medium">{user?.name || 'Não informado'}</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-300">E-mail</Label>
                  <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-2 flex items-center justify-between">
                    <p className="text-gray-400 text-sm">
                      {showEmail ? (user?.email || 'Não informado') : '***@***.***'}
                    </p>
                    <button
                      onClick={() => setShowEmail(!showEmail)}
                      className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                      type="button"
                    >
                      {showEmail ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {isEditing && <p className="text-xs text-gray-500 mt-1">O e-mail não pode ser alterado.</p>}
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-300">Telefone</Label>
                  {isEditing ? (
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="bg-white/5 border-white/10 text-white mt-2 rounded-lg"
                      placeholder="(11) 99999-9999"
                    />
                  ) : (
                    <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-2 flex items-center justify-between">
                      <p className="text-white font-medium">
                        {showPhone
                          ? (user?.phone && user.phone.trim() !== '' ? user.phone : 'Não informado')
                          : (user?.phone && user.phone.trim() !== '' ? '(••) •••••-••••' : 'Não informado')
                        }
                      </p>
                      {user?.phone && user.phone.trim() !== '' && (
                        <button
                          onClick={() => setShowPhone(!showPhone)}
                          className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                          type="button"
                        >
                          {showPhone ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-300">ID do Discord</Label>
                  <div className="mt-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <p className="text-blue-400 font-mono text-sm">
                        {showDiscordId ? user?.discordId : '••••••••••••••••••'}
                      </p>
                      <button
                        onClick={() => setShowDiscordId(!showDiscordId)}
                        className="text-blue-400 hover:text-blue-300 transition-colors duration-200"
                        type="button"
                      >
                        {showDiscordId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-blue-300 mt-1">🔒 Não editável</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferências de Notificação */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Card className="bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center text-xl">
                  <div className="p-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg mr-3">
                    <Bell className="h-5 w-5 text-yellow-400" />
                  </div>
                  Preferências de Notificação
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Escolha como você deseja ser notificado sobre atualizações
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base text-gray-200">Permitir Notificações</Label>
                    <p className="text-sm text-gray-500">Exibir o sino de notificações e alertas no painel</p>
                  </div>
                  <Switch
                    checked={formData.notificationsEnabled}
                    onCheckedChange={(checked) => handleInputChange('notificationsEnabled', checked)}
                    disabled={!isEditing}
                  />
                </div>

                <Separator className="bg-white/5" />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base text-gray-200">Sons de Notificação</Label>
                    <p className="text-sm text-gray-500">Reproduzir aviso sonoro ao receber novas notificações</p>
                  </div>
                  <Switch
                    checked={formData.notificationSounds}
                    onCheckedChange={(checked) => handleInputChange('notificationSounds', checked)}
                    disabled={!isEditing || !formData.notificationsEnabled}
                  />
                </div>

                <Separator className="bg-white/5" />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base text-gray-200">Exibir Comunicados</Label>
                    <p className="text-sm text-gray-500">Notificar sobre novos comunicados importantes do servidor</p>
                  </div>
                  <Switch
                    checked={formData.showAnnouncements}
                    onCheckedChange={(checked) => handleInputChange('showAnnouncements', checked)}
                    disabled={!isEditing || !formData.notificationsEnabled}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Backup de Dados (LGPD) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center text-xl">
                  <div className="p-2 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-lg mr-3">
                    <ShieldCheck className="h-5 w-5 text-green-400" />
                  </div>
                  Privacidade e Dados (LGPD)
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Transparência e controle sobre suas informações pessoais no sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-white/5 border border-white/5 rounded-xl space-y-3">
                  <p className="text-sm text-gray-300 leading-relaxed">
                    Em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD)</strong>, você tem o direito de acessar todos os seus dados pessoais armazenados em nossa plataforma, incluindo logs de atividade, histórico de tickets e informações de perfil.
                  </p>
                  <Button
                    onClick={handleDownloadBackup}
                    variant="outline"
                    className="w-full sm:w-auto bg-white/5 hover:bg-white/10 border-white/10 text-white transition-all duration-300"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar todos os meus dados (JSON)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
};

export default UserSettings;
